import React, { forwardRef, useId } from 'react';
import { Check } from 'lucide-react';

/**
 * Tappable checkbox card. register()-compatible:
 *   <CheckboxCard label="H - Placarded Hazmat" value="H" {...register('endorsements')} />
 * Renders a real (visually hidden) checkbox so forms stay accessible.
 */
const CheckboxCard = forwardRef(({ label, description, className = '', id: idProp, name, disabled, ...rest }, ref) => {
  const generatedId = useId();
  const id = idProp || `${name || 'checkbox'}-${generatedId}`;

  return (
    <label
      htmlFor={id}
      className={[
        'group relative flex cursor-pointer items-start gap-3 rounded-lg border border-gray-300 bg-white p-3 transition-colors',
        'hover:border-blue-400 has-[:checked]:border-blue-600 has-[:checked]:bg-blue-50',
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-500 has-[:focus-visible]:ring-offset-1',
        disabled ? 'cursor-not-allowed opacity-60' : '',
        className
      ].join(' ')}
    >
      <input ref={ref} id={id} name={name} type="checkbox" disabled={disabled} className="peer sr-only" {...rest} />
      <span
        aria-hidden="true"
        className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border border-gray-400 bg-white text-white peer-checked:border-blue-600 peer-checked:bg-blue-600"
      >
        <Check className="h-3.5 w-3.5 opacity-0 peer-checked:opacity-100 group-has-[:checked]:opacity-100" strokeWidth={3} />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-gray-900">{label}</span>
        {description && <span className="mt-0.5 block text-xs text-gray-500">{description}</span>}
      </span>
    </label>
  );
});

CheckboxCard.displayName = 'CheckboxCard';

/** Plain inline checkbox with label (for consents and certifications). */
export const CheckboxInline = forwardRef(({ label, error, className = '', id: idProp, name, children, ...rest }, ref) => {
  const generatedId = useId();
  const id = idProp || `${name || 'checkbox'}-${generatedId}`;
  return (
    <div className={className}>
      <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
        <input
          ref={ref}
          id={id}
          name={name}
          type="checkbox"
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className="mt-1 h-5 w-5 flex-shrink-0 rounded border-gray-300 accent-blue-600 focus:ring-2 focus:ring-blue-500"
          {...rest}
        />
        <span className="text-sm text-gray-800">{label || children}</span>
      </label>
      {error && <p id={`${id}-error`} className="mt-1 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
});

CheckboxInline.displayName = 'CheckboxInline';

export default CheckboxCard;
